import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Available languages in the application
 */
export const AVAILABLE_LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
] as const;

export type LanguageCode = typeof AVAILABLE_LANGUAGES[number]['code'];

/**
 * Language context type
 */
interface LanguageContextType {
  currentLanguage: LanguageCode;
  changeLanguage: (lang: LanguageCode) => Promise<void>;
  availableLanguages: typeof AVAILABLE_LANGUAGES;
}

/**
 * Language context
 */
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Language provider props
 */
interface LanguageProviderProps {
  children: ReactNode;
}

/**
 * Language provider component
 * Manages the current language state and provides language switching functionality
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(
    (i18n.language as LanguageCode) || 'de'
  );

  /**
   * Change the current language
   */
  const changeLanguage = async (lang: LanguageCode) => {
    try {
      await i18n.changeLanguage(lang);
      setCurrentLanguage(lang);
      localStorage.setItem('i18nextLng', lang);

      // Update HTML lang attribute for accessibility
      document.documentElement.lang = lang;

      // Force page reload to ensure all components update
      window.location.reload();
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  /**
   * Sync language state with i18n on mount and language change
   */
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLanguage(lng as LanguageCode);
      document.documentElement.lang = lng;
    };

    i18n.on('languageChanged', handleLanguageChanged);

    // Set initial language
    handleLanguageChanged(i18n.language);

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  return (
    <LanguageContext.Provider 
      value={{ 
        currentLanguage, 
        changeLanguage, 
        availableLanguages: AVAILABLE_LANGUAGES 
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to use language context
 * @throws Error if used outside of LanguageProvider
 */
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

